import { TezosToolkit } from '@taquito/taquito';
import { Tzip16Module, tzip16 } from '@taquito/tzip16';
import NETWORKS from './networkconfig.js';

// Decode hex string to ASCII/UTF-8
function decodeHex(hex) {
  if (!hex) return "";
  let hexStr = hex.startsWith('0x') ? hex.slice(2) : hex;
  if (!/^[0-9a-fA-F]*$/.test(hexStr)) return "";
  // Convert hex to Uint8Array
  const bytes = new Uint8Array(hexStr.length / 2);
  for (let i = 0; i < hexStr.length; i += 2) {
    bytes[i / 2] = parseInt(hexStr.substr(i, 2), 16);
  }
  // Decode as UTF-8
  try {
    return new TextDecoder("utf-8").decode(bytes);
  } catch (e) {
    // Fallback: return ASCII as before
    let decoded = '';
    for (let i = 0; i < hexStr.length; i += 2) {
      decoded += String.fromCharCode(parseInt(hexStr.substr(i, 2), 16));
    }
    return decoded;
  }
}

export async function fetchGalleryTokenIds(network, contractAddress) {
  const rpc = NETWORKS[network].rpc;
  const tezos = new TezosToolkit(rpc);
  tezos.addExtension(new Tzip16Module());

  const contractInstance = await tezos.contract.at(contractAddress, tzip16);
  const metadataViews = await contractInstance.tzip16().metadataViews();

  const tokenIds = await metadataViews.get_active_tokens().executeView();
  if (!Array.isArray(tokenIds) || !tokenIds.length) return [];

  // Convert all tokenIds to JS numbers (safe for NFT/FA2 IDs)
  const numberTokenIds = tokenIds.map(tid =>
    tid && typeof tid.toNumber === 'function' ? tid.toNumber() : Number(tid)
  );
  numberTokenIds.sort((a,b) => b - a);
  return numberTokenIds;
}

export async function fetchGalleryTokensOwned(network, contractAddress, ownerAddress) {
  const rpc = NETWORKS[network].rpc;
  const tezos = new TezosToolkit(rpc);
  tezos.addExtension(new Tzip16Module());

  const contractInstance = await tezos.contract.at(contractAddress, tzip16);
  const metadataViews = await contractInstance.tzip16().metadataViews();

  const tokenIds = await metadataViews.get_active_tokens().executeView();

  const ownedTokenIds = [];

  for (const tokenId of tokenIds) {
    try {
      // Call get_balance view: input { owner: address, token_id: number }
      const balance = await metadataViews.get_balance().executeView([ ownerAddress, tokenId ]);
      if (balance && balance.toNumber && balance.toNumber() > 0) {
        ownedTokenIds.push(tokenId);
      }
    } catch (err) {
      console.warn(`Failed to get balance for tokenId ${tokenId}:`, err);
    }
  }

  ownedTokenIds.sort((a,b) => b - a);
  return ownedTokenIds;
}


export async function fetchGalleryTokenMetadata(tokenId, network, contractAddress) {
  const rpc = NETWORKS[network].rpc;
  const tezos = new TezosToolkit(rpc);
  tezos.addExtension(new Tzip16Module());

  const contractInstance = await tezos.contract.at(contractAddress, tzip16);
  const metadataViews = await contractInstance.tzip16().metadataViews();
  const meta = await metadataViews.get_token_metadata().executeView(tokenId);

  const info = meta.token_info;
  let get = (k) => (typeof info.get === 'function' ? info.get(k) : info[k]);

  const nameHex = get('name');
  const artifactUriHex = get('artifactUri');
  const authorsHex = get('authors');
  const creatorsHex = get('creators');
  const royaltiesHex = get('royalties'); // New optional field

  const name = decodeHex(nameHex);
  const img = decodeHex(artifactUriHex);
  let authors = [];
  let creators = [];
  let royalties = '';

  try { authors = JSON.parse(decodeHex(authorsHex) || '[]'); } catch {}
  try { creators = JSON.parse(decodeHex(creatorsHex) || '[]'); } catch {}
  try {
    const decoded = decodeHex(royaltiesHex);
    if (decoded && decoded.trim()) {
      royalties = formatRoyaltiesFromMetadata(decoded.trim());
    }
  } catch {}

  return { tokenId, name, authors, creators, img, royalties };
}

/*
export async function fetchArtistForToken(network, contractAddress, tokenId) {
  const rpc = NETWORKS[network].rpc;
  const tezos = new TezosToolkit(rpc);
  tezos.addExtension(new Tzip16Module());

  try {
    const contract = await tezos.contract.at(contractAddress, tzip16);
    const metadataViews = await contract.tzip16().metadataViews();
    const tokenMetadata = await metadataViews.get_token_metadata().executeView(Number(tokenId));

	if (tokenMetadata && tokenMetadata.token_info) {
	  let artistOrCreator = null;

	  if (typeof tokenMetadata.token_info.get === 'function') {
		// token_info is Map
		if (tokenMetadata.token_info.has('creators')) {
		  const creators = tokenMetadata.token_info.get('creators');
		  if (Array.isArray(creators) && creators.length > 0) {
			artistOrCreator = creators[0];
		  }
		} else {
		  artistOrCreator = tokenMetadata.token_info.get('artist') || tokenMetadata.token_info.get('creator');
		}
	  } else if (typeof tokenMetadata.token_info === 'object') {
		// token_info is plain object
		if (tokenMetadata.token_info.creators && Array.isArray(tokenMetadata.token_info.creators)) {
		  artistOrCreator = tokenMetadata.token_info.creators[0];
		} else {
		  artistOrCreator = tokenMetadata.token_info.artist || tokenMetadata.token_info.creator;
		}
	  }

	  if (artistOrCreator) {
		// If artistOrCreator is hex encoded, decode; else assume address string directly
		if (typeof artistOrCreator === 'string' && artistOrCreator.startsWith('0x')) {
		  return decodeHex(artistOrCreator);
		} else {
		  return artistOrCreator;
		}
	  }
	  console.log("address:", artistOrCreator);
	}
  } catch(err) {
    console.warn('Failed to fetch artist metadata:', err);
  }
  return '';
}
*/

export async function isWalletOwnerOfToken(address, tokenId, network, contractAddress) {
  try {
    const rpc = NETWORKS[network].rpc;
    const tezos = new TezosToolkit(rpc);
    tezos.addExtension(new Tzip16Module());
    const contractInstance = await tezos.contract.at(contractAddress, tzip16);
    const metadataViews = await contractInstance.tzip16().metadataViews();

    // Pass parameters separately (not as single array)
    const balance = await metadataViews.get_balance().executeView(address, Number(tokenId));

    if (balance && typeof balance.toNumber === 'function') {
      return balance.toNumber() > 0;
    }
    if (typeof balance === 'number') {
      return balance > 0;
    }
    return false;

  } catch (err) {
    console.warn('Failed to call get_balance offchain view:', err);
    return false;
  }
}
